import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Sparkles, Shield, Sun } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useProducts } from '../contexts/ProductsContext';
import ReviewsSection from '../components/ReviewsSection';
import { cloudinaryUrl, cloudinarySrcSet } from '../lib/cloudinary';
import ProductCard from '../components/ProductCard';

const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }

function HeroSection() {
  const { settings, waLink } = useSettings();
  const heroImg = resolveUrl(settings?.hero_banner_desktop) ||
    'https://res.cloudinary.com/doaatitvq/image/upload/v1776230430/20260415_1048_Elegant_Necklace_Portrait_remix_01kp7s6jsbfpmbdaw8559dsz2t_dqelob.png';
  return (
    <section data-testid="hero-section" className="relative min-h-[70vh] md:min-h-screen flex items-end md:items-center overflow-hidden pb-12 md:pb-0">
      <div className="absolute inset-0">
        <img src={cloudinaryUrl(heroImg, { width: 1200 })} srcSet={cloudinarySrcSet(heroImg, [600, 900, 1400])} sizes="100vw" alt="KIRAQ Hero" className="w-full h-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F6F3EE]/95 via-[#F6F3EE]/75 to-[#F6F3EE]/20 md:to-transparent" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 lg:px-24 w-full py-20 md:py-0">
        <div className="max-w-md md:max-w-xl">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-3 md:mb-5">Simply Rare</p>
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-6xl font-light tracking-tighter text-[#1E1C1A] leading-[1.1] mb-4 md:mb-6">
            Not mass produced.<br/>Just 1 piece each.
          </h1>
          <p className="text-sm md:text-base font-light leading-relaxed tracking-wide text-[#7A7369] mb-6 md:mb-8 max-w-sm">
            Shop trendy and affordable jewellery for women and men in India.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/shop" className="bg-[#1E1C1A] text-[#F6F3EE] px-6 py-3 text-[10px] md:text-xs tracking-[0.2em] uppercase hover:bg-[#3A3632] transition-colors flex items-center gap-2">
              Explore Collection <ArrowRight size={13} />
            </Link>
            <a href={waLink("Hi KIRAQ, I'd like to know more about your pieces")} target="_blank" rel="noopener noreferrer" className="border border-[#1E1C1A] text-[#1E1C1A] hover:bg-[#1E1C1A] hover:text-[#F6F3EE] px-5 py-3 text-[10px] md:text-xs tracking-[0.15em] uppercase flex items-center gap-2 transition-all">
              <MessageCircle size={13} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FirstDropSection({ products }) {
  return (
    <section data-testid="first-drop-section" className="py-10 md:py-20 px-4 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4 md:mb-8">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-2">First Drop</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A] mb-1.5">Something Worth Wearing</h2>
          <p className="text-xs md:text-sm font-light text-[#7A7369]">Curated pieces from our first drop.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
          {products.slice(0, 8).map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} thumbWidth={480} sizes="(max-width:768px) 50vw, (max-width:1200px) 33vw, 25vw" />
          ))}
        </div>
        <div className="text-center mt-6 md:mt-10">
          <Link to="/shop" className="inline-flex items-center gap-2 text-[10px] md:text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] border-b border-[#1E1C1A] pb-0.5 hover:text-[#C6A978] hover:border-[#C6A978] transition-colors">
            View All Pieces <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CollectionsSection({ collections }) {
  const { settings } = useSettings();
  return (
    <section id="collections" data-testid="collections-section" className="py-10 md:py-20 px-4 md:px-12 lg:px-24 bg-[#E8E1D8]/40">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6 md:mb-12">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-2">Our World</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A]">Collections</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-8">
          {collections.slice(0, 3).map((col, i) => {
            const customImg = settings?.collection_images?.[col.slug];
            const imgSrc = resolveUrl(customImg) || resolveUrl(col.image_url) || '';
            return (
              <Link key={col.id} to={`/collection/${col.slug}`} data-testid={`collection-card-${i}`} className="group block">
                <div className="relative overflow-hidden bg-[#E8E1D8] aspect-[3/4] mb-3 md:mb-4">
                  <img src={cloudinaryUrl(imgSrc, { width: 500, height: 667, quality: 'eco' })} srcSet={cloudinarySrcSet(imgSrc, [400, 600, 800])} sizes="(max-width:768px) 100vw, 33vw" alt={col.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading={i < 2 ? 'eager' : 'lazy'} decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E1C1A]/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-['Cormorant_Garamond'] text-base md:text-xl font-light text-[#F6F3EE] tracking-tight mb-0.5">{col.name}</h3>
                    <p className="text-[9px] md:text-[10px] uppercase tracking-[0.15em] text-[#C6A978] font-medium">Explore ↗</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyKiraqSection() {
  const pillars = [
    { icon: <Sparkles size={20} />, title: 'Minimal Design', desc: 'Every detail is intentional. Nothing extra, nothing missing.' },
    { icon: <Shield size={20} />, title: 'Anti-Tarnish Steel', desc: 'Gold-finish stainless steel built to last everyday wear.' },
    { icon: <Sun size={20} />, title: 'Made for Everyday', desc: 'Pieces that move with you — from morning coffee to midnight plans.' },
  ];
  return (
    <section className="py-10 md:py-20 px-4 md:px-12 lg:px-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-2">The KIRAQ Way</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A]">Why KIRAQ</h2>
        </div>
        <div className="grid grid-cols-3 gap-4 md:gap-10">
          {pillars.map((p, i) => (
            <div key={i} className="text-center">
              <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 flex items-center justify-center text-[#C6A978]">{p.icon}</div>
              <h3 className="font-['Cormorant_Garamond'] text-sm md:text-lg font-medium text-[#1E1C1A] mb-1.5">{p.title}</h3>
              <p className="text-[10px] md:text-xs font-light text-[#7A7369] leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MediaItem({ url, i }) {
  const videoRef = useRef(null);
  const isVideo = url?.match(/\.(mp4|webm|mov)$/i);
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) videoRef.current?.play();
      else videoRef.current?.pause();
    }, { threshold: 0.5 });
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [isVideo]);
  return (
    <div className="aspect-square overflow-hidden group cursor-pointer">
      {isVideo ? (
        <video ref={videoRef} muted loop playsInline preload="none" src={resolveUrl(url)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <img src={cloudinaryUrl(resolveUrl(url), { width: 240, height: 240, quality: 'eco' })} srcSet={cloudinarySrcSet(resolveUrl(url), [160, 240, 360])} sizes="(max-width:768px) 33vw, 17vw" alt={`KIRAQ style ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      )}
    </div>
  );
}

function InstagramSection() {
  const { settings } = useSettings();
  const imgs = settings?.instagram_images?.length > 0 ? settings.instagram_images : [
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1776281658/20260219_2237_Image_Generation_simple_compose_01khvdxcv8exaac4jwky0yw5jf_m18x4q.png",
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1778833968/ChatGPT_Image_May_10_2026_12_41_58_AM_xkx35o.png",
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1776281658/20260219_2240_Image_Generation_simple_compose_01khve26tresc8nbq4r2apxtas_mnv2xw.png",
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1778833966/ChatGPT_Image_May_10_2026_12_41_36_AM_lbjjks.png",
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1776281657/20260219_2234_Refined_Editorial_Elegance_simple_compose_01khvdpndnem2v0pcygw7js8wx_b2vpbw.png",
    "https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1776281831/2_bpuo5j.png",
  ];
  return (
    <section className="py-10 md:py-20 px-4 md:px-12 lg:px-24 bg-[#E8E1D8]/40">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-5 md:mb-10">
          <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-2">@kiraq</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-light tracking-tight text-[#1E1C1A]">From Our World</h2>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 md:gap-3">
          {imgs.slice(0, 6).map((url, i) => <MediaItem key={i} url={url} i={i} />)}
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="py-10 md:py-20 px-4 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] md:aspect-[3/4] overflow-hidden">
              <img src={cloudinaryUrl("https://res.cloudinary.com/doaatitvq/image/upload/f_auto,q_auto/v1776281996/20260416_0103_Image_Generation_remix_01kp9a469gf3a916gr1fxc3n29_gaztjm.png", { width: 600, quality: 'eco' })} alt="About KIRAQ" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-4">
            <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-3">Our Story</p>
            <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A] mb-4 leading-tight">Designed to Elevate<br/>Your Everyday</h2>
            <p className="text-xs md:text-sm font-light leading-relaxed tracking-wide text-[#7A7369] mb-4">KIRAQ was born from a singular vision: to create jewellery that whispers rather than shouts. Each piece is crafted with intention — minimal, premium, and made for the life you live.</p>
            <Link to="/about" className="text-[10px] md:text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] border-b border-[#1E1C1A] pb-0.5 hover:text-[#C6A978] hover:border-[#C6A978] transition-colors">Read Our Story</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppCTA() {
  const { waLink } = useSettings();
  return (
    <section className="py-10 md:py-20 px-4 md:px-12 lg:px-24 bg-[#E8E1D8]">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-3">Personal Shopping</p>
        <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl font-light tracking-tight text-[#1E1C1A] mb-3">Need Help Choosing?</h2>
        <p className="text-xs md:text-sm font-light leading-relaxed text-[#7A7369] mb-6 max-w-md mx-auto">Our team is here to help you find the perfect piece. Reach out via WhatsApp for personalised recommendations.</p>
        <a href={waLink("Hi KIRAQ, I need help choosing a piece")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#1E1C1A] text-[#1E1C1A] hover:bg-[#1E1C1A] hover:text-[#F6F3EE] px-6 py-3 text-[10px] md:text-xs tracking-[0.2em] uppercase transition-all">
          <MessageCircle size={14} /> Chat With Us
        </a>
      </div>
    </section>
  );
}

export default function HomePage() {
  // Zero API calls — data is instant from ProductsContext (pre-loaded static)
  const { featured, collections } = useProducts();
  return (
    <main data-testid="home-page">
      <HeroSection />
      <FirstDropSection products={featured} />
      <CollectionsSection collections={collections} />
      <WhyKiraqSection />
      <InstagramSection />
      <AboutSection />
      <ReviewsSection title="Loved by Our Community" showForm={false} limit={6} />
      <WhatsAppCTA />
    </main>
  );
}